import { FC } from 'react';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import InfoOutline from '@mui/icons-material/InfoOutline';
import { Controller } from 'react-hook-form';
import { KycRequestProcess } from 'further-types/lp/kyc';
import { Column, Row } from 'further-ui/components/Layout';
import BasicSelect from 'further-ui/components/BasicSelect';
import useKycSettings from './useKycSettings';
import BasicCard from '../BasicCard';
import CustomKycRequests from './CustomKycRequests';

type Props = {
  vehicleId: string;
};

const KycSettings: FC<Props> = ({ vehicleId }) => {
  const {
    control,
    kycRequestProcess,
    kycRequestProcessOptions,
    handleKycRequestProcessChange,
  } = useKycSettings(vehicleId);

  return (
    <Column fullWidth spacing="lg">
      <Column spacing="sm">
        <Row centered spacing="xxs">
          <Typography variant="h6">KYC request process</Typography>
          <Tooltip title="Select your individual investor KYC requests. eKYC requires users to complete electronic KYC (single ID and video of user with automatic checks against international AML databases). Information request is used where you wish to request KYC information or documentation after (or instead of) individual investor eKYC.">
            <InfoOutline fontSize="inherit" />
          </Tooltip>
        </Row>
        <Controller
          control={control}
          name="kycRequestProcess"
          render={({ field }) => (
            <BasicSelect
              options={kycRequestProcessOptions}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                handleKycRequestProcessChange(value);
              }}
            />
          )}
        />
      </Column>

      <Divider />
      {kycRequestProcess === KycRequestProcess.InformationRequestOnly ||
      kycRequestProcess === KycRequestProcess.EKycAndInformationRequest ? (
        <BasicCard
          title="KYC information requests"
          description="Set up KYC onboarding requests required from investors. Create individual requests, group related ones into sections, and adjust their order with drag and drop to align with your review process."
        />
      ) : null}
      {kycRequestProcess === KycRequestProcess.EKycOnly ||
      kycRequestProcess === KycRequestProcess.EKycAndInformationRequest ? (
        <BasicCard
          title="eKYC request"
          description="The system's automated eKYC flow will request an identity document and check the investor against international AML databases globally."
          outlined
          number={
            kycRequestProcess === KycRequestProcess.EKycAndInformationRequest
              ? 1
              : undefined
          }
        />
      ) : null}
      {kycRequestProcess === KycRequestProcess.InformationRequestOnly ||
      kycRequestProcess === KycRequestProcess.EKycAndInformationRequest ? (
        <BasicCard
          title="KYC information"
          description="As a prospective private fund investor, we must first carry out Know Your Customer checks. To do this, please submit your information below."
          outlined
          number={
            kycRequestProcess === KycRequestProcess.EKycAndInformationRequest
              ? 2
              : undefined
          }
        />
      ) : null}
      {kycRequestProcess === KycRequestProcess.InformationRequestOnly ||
      kycRequestProcess === KycRequestProcess.EKycAndInformationRequest ? (
        <>
          <Divider />
          <CustomKycRequests vehicleId={vehicleId} />
        </>
      ) : null}
    </Column>
  );
};

export default KycSettings;
